import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/AdminDashboard.css';

const ActiveUsersWidget = () => {
  const [activeUsers, setActiveUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchActiveUsers();
    fetchStats();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchActiveUsers();
      fetchStats();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const groupSessionsByUser = (sessions) => {
    const grouped = new Map();
    sessions.forEach((session) => {
      const userId = session.user_id;
      if (!grouped.has(userId)) {
        grouped.set(userId, {
          id: session.User?.id || userId,
          name: session.User?.name || 'Unknown',
          role: session.User?.role || 'Unknown',
          devices: []
        });
      }
      grouped.get(userId).devices.push({
        session_id: session.session_id,
        user_agent: session.user_agent,
        ip_address: session.ip_address,
        last_activity: session.last_activity
      });
    });

    return Array.from(grouped.values()).map((user) => ({
      ...user,
      devices: user.devices.sort((a, b) => new Date(b.last_activity) - new Date(a.last_activity))
    }));
  };

  const fetchActiveUsers = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/user-activity/active-users`
      );
      const sessions = response.data.sessions || [];
      const groupedUsers = groupSessionsByUser(sessions);
      setActiveUsers(groupedUsers);
      setError(null);
    } catch (err) {
      console.error('Error fetching active users:', err);
      setError('Failed to fetch active users');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/user-activity/activity-stats?period=24h`
      );
      setStats(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError('Failed to fetch statistics');
      setLoading(false);
    }
  };

  const formatLastActivity = (timestamp) => {
    const now = new Date();
    const lastActivity = new Date(timestamp);
    const diffMinutes = Math.floor((now - lastActivity) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return lastActivity.toLocaleDateString();
  };

  const formatIPAddress = (ip) => {
    if (!ip) return 'Unknown';
    if (ip === '::1' || ip === '127.0.0.1') return 'localhost';
    if (ip.startsWith('192.168.') || ip.startsWith('10.') || ip.startsWith('172.')) {
      return `${ip} (local)`;
    }
    return ip;
  };

  const parseUserAgent = (userAgent) => {
    if (!userAgent) return 'Unknown Device';
    
    console.log('Parsing user agent:', userAgent); // Debug log
    
    // Mobile device detection first
    if (userAgent.includes('iPhone')) return 'iPhone';
    if (userAgent.includes('iPad')) return 'iPad';
    if (userAgent.includes('Android') && userAgent.includes('Mobile')) return 'Android Phone';
    if (userAgent.includes('Android')) return 'Android Tablet';
    
    // Operating system detection
    const osInfo = [];
    if (userAgent.includes('Windows NT 10')) osInfo.push('Windows 10');
    else if (userAgent.includes('Windows NT 6.3')) osInfo.push('Windows 8.1');
    else if (userAgent.includes('Windows NT 6.1')) osInfo.push('Windows 7');
    else if (userAgent.includes('Windows')) osInfo.push('Windows');
    else if (userAgent.includes('Mac OS X') || userAgent.includes('macOS')) osInfo.push('macOS');
    else if (userAgent.includes('Linux')) osInfo.push('Linux');
    
    // Browser detection
    let browser = '';
    if (userAgent.includes('Edg/')) browser = 'Edge';
    else if (userAgent.includes('Chrome/') && !userAgent.includes('Edg/')) browser = 'Chrome';
    else if (userAgent.includes('Firefox/')) browser = 'Firefox';
    else if (userAgent.includes('Safari/') && !userAgent.includes('Chrome/')) browser = 'Safari';
    else if (userAgent.includes('Opera/') || userAgent.includes('OPR/')) browser = 'Opera';
    else browser = 'Unknown Browser';
    
    // Combine OS and browser info
    if (osInfo.length > 0) {
      return `${browser} on ${osInfo[0]}`;
    }
    
    return browser || 'Desktop';
  };

  if (loading) return <div className="active-users-widget loading">Loading...</div>;

  return (
    <div className="active-users-widget">
      <div className="widget-header">
        <h3>Active Users</h3>
        <span className="refresh-indicator">
          Auto-refreshes every 30s
        </span>
      </div>

      <div className="usage-stats">
        <div className="stat-item">
          <span className="stat-number">{stats.currently_active || 0}</span>
          <span className="stat-label">Currently Active</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{stats.unique_users || 0}</span>
          <span className="stat-label">Today's Users</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{stats.total_sessions || 0}</span>
          <span className="stat-label">Today's Sessions</span>
        </div>
      </div>

      {error && (
        <div className="error-message">{error}</div>
      )}

      <div className="active-users-list">
        <h4>Currently Active ({activeUsers.length})</h4>
        {activeUsers.length === 0 ? (
          <p className="no-users">No users currently active</p>
        ) : (
          <div className="users-grid">
            {activeUsers.map((user) => (
              <div key={user.id} className="user-card">
                <div className="admin-user-info">
                  <span className="admin-user-name">{user.name}</span>
                  <span className="admin-user-role">{user.role}</span>
                </div>
                <div className="admin-user-activity">
                  {user.devices.map((device) => (
                    <div key={device.session_id} className="admin-device-row">
                      <span className="admin-user-device">{parseUserAgent(device.user_agent)}</span>
                      <span className="admin-last-activity">
                        {formatLastActivity(device.last_activity)}
                      </span>
                      <span className="admin-user-ip">{formatIPAddress(device.ip_address)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActiveUsersWidget;
